/**
 * @module ServiceTestVerifyHelper
 * @description Phase 14 orchestrator for verify strategy resolution, polling, and normalized error handling.
 * Preserves the legacy entry point while delegating to extracted strategy modules.
 */

import { t } from '@lingui/macro';
import { get, keyBy } from 'lodash';
import verifyScriptedStrategy from './VerifyScriptedStrategy';
import verifyVoIPStrategy from '../sections/voip/verify/VerifyVoIPStrategy';
import verifyWebStrategy from './VerifyWebStrategy';
import { buildVerifyErrorResult } from './VerifyErrorHandler';
import { NATIVE_TEST } from './VerifySharedConstants';
import { pollForResults } from './VerifyPolling';

/**
 * Verify orchestration: resolveStrategy() picks the correct strategy object (Scripted/Web/VoIP)
 * based on test type, then runServiceTestVerify() executes: build payload → POST quicktest → poll for
 * results → build verify result. Each strategy implements the VerifyStrategy interface below.
 */

/**
 * @typedef {Object} VerifyStrategy
 * @property {Function} buildVerifyPayload - (formData, testType, agents, dataService?) => payload | Promise<payload>
 * @property {Function} checkStatus - (response) => { isSuccessful, stillRunning }
 * @property {Function} verifyHasResults - (response) => boolean
 * @property {Function} buildVerifyResult - (isSuccessful, response, getAgentName) => verifyResult
 * @property {{ pollFor: number, pollIntervalMs: number }} pollConfig
 * @property {Function} [pollingCounter] - (pollState) => pollState (optional dynamic polling)
 * @property {boolean} [isAsync] - true if buildVerifyPayload returns a Promise
 */

const QUICKTEST_API = '/ipm/v1/admin/quicktests';

/**
 * Resolve the verify strategy for a given test type.
 * portal/ui source: verify.service.js testTypeService()
 *
 * @param {string} testTypeName - The test type name (e.g. 'Web', 'VoipPulse')
 * @returns {VerifyStrategy}
 */
export function resolveStrategy(testTypeName) {
    switch (testTypeName) {
        case NATIVE_TEST.Web:
            return verifyWebStrategy;
        case NATIVE_TEST.VoipPulseToPulse:
        case NATIVE_TEST.VoipPulseToPhone:
            return verifyVoIPStrategy;
        default:
            return verifyScriptedStrategy;
    }
}

/**
 * Build agent name resolver from agent list.
 * portal/ui source: verify.service.js getAgentName()
 *
 * @param {Array} agents
 * @returns {Function} (agentId) => displayName
 */
function buildAgentNameResolver(agents) {
    const indexed = keyBy(agents || [], '_id');
    return (agentId) => {
        if (!agentId) return `Unknown Name for ID: ${agentId}`;
        const agent = indexed[agentId];
        return agent?.name || `Unknown Name for ID: ${agentId}`;
    };
}

/**
 * Run a service test verify — the main entry point.
 *
 * Pattern mirrors VoipVerifyHelper.runVoipDialToneVerify:
 * 1. Build payload via strategy
 * 2. POST to quicktests API
 * 3. Poll for results
 * 4. Build and set final result
 *
 * @param {Object} params
 * @param {Object} params.formData - Current form values (from getValues + formData merge)
 * @param {Object} params.testType - Test type configuration object
 * @param {Array}  params.agents - Available agents (from matchingAgentsRef.current)
 * @param {string} params.userName - Current user display name
 * @param {string} params.userId - Current user ID
 * @param {Object} params.dataService - useDataService() instance
 * @param {Function} params.setShowVerify - State setter for showVerify
 * @param {Function} params.setVerifyLoading - State setter for verifyLoading
 * @param {Function} params.setVerifyResult - State setter for verifyResult
 * @param {string} [params.testId] - Existing test _id (for re-verify)
 * @param {AbortSignal} [params.signal] - AbortSignal to cancel polling when component unmounts
 */
export async function runServiceTestVerify({
    formData,
    testType,
    agents,
    userName,
    userId,
    dataService,
    setShowVerify,
    setVerifyLoading,
    setVerifyResult,
    testId,
    signal,
    verifyResultSectionRef,
}) {
    const strategy = resolveStrategy(testType.name);
    const getAgentName = buildAgentNameResolver(agents);

    setShowVerify(true);
    setVerifyLoading(true);
    setVerifyResult(null);

    // Auto-scroll to the verify section after Collapse animation expands.
    // Matches portal/ui behavior: page scrolls to verify results on verify click.
    setTimeout(() => {
        if (verifyResultSectionRef.current) {
            verifyResultSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, 350);

    try {
        // 1. Build the payload via strategy
        let payload;
        if (strategy.isAsync) {
            payload = await strategy.buildVerifyPayload(formData, testType, agents, dataService);
        } else {
            payload = strategy.buildVerifyPayload(formData, testType, agents);
        }

        // 2. Add verify metadata (from quickTests.service.js saveVerifyTest)
        payload.verify = true;
        payload.createdBy = userName;
        payload.createdById = userId;
        payload._id = null;
        payload.testId = testId || formData._id || null;

        // 3. Show "Configuring..." status
        setVerifyResult({
            status: t`Processing`,
            statusMsg: t`Configuring nPoints to verify configuration...`,
            resultType: null,
            resultData: null,
        });

        // 4. POST the verify test
        const response = await dataService.post(QUICKTEST_API, payload, null, { signal });
        const verifyId = get(response, '_id');

        if (!verifyId) {
            // Immediate response without polling (edge case)
            setVerifyResult({
                status: get(response, 'status', t`Success`),
                statusMsg: get(response, 'statusMsg', get(response, 'message', t`Verify completed.`)),
                resultType: null,
                resultData: null,
            });
            return;
        }

        // 5. Show "Processing..." and start polling
        setVerifyResult({
            status: t`Processing`,
            statusMsg: t`Configuring nPoints to verify configuration...`,
            resultType: null,
            resultData: null,
        });

        // 6. Poll for results
        await pollForResults({
            verifyId,
            strategy,
            testType,
            getAgentName,
            dataService,
            setVerifyResult,
            signal,
        });
    } catch (error) {
        // Silently exit if cancelled (user navigated away)
        if (error?.name === 'AbortError') return;
        setVerifyResult(buildVerifyErrorResult(error));
    } finally {
        if (!signal?.aborted) {
            setVerifyLoading(false);
        }
    }
}
